import { useRef, useState } from 'react';

export type EditFormFieldState = {
  readonly modified: boolean;
  readonly messages: readonly string[];
};

const PRISTINE_FIELD_STATE: EditFormFieldState = { modified: false, messages: [] };

function toBaselineMap<TFieldName extends string>(
  baselineValues: Partial<Record<TFieldName, string>> | undefined,
): Map<TFieldName, string> {
  const baselines = new Map<TFieldName, string>();
  for (const [field, value] of Object.entries(baselineValues ?? {})) {
    if (typeof value === 'string') {
      baselines.set(field as TFieldName, value);
    }
  }
  return baselines;
}

export function getFieldCssClass(state: EditFormFieldState): string {
  const isValid = state.messages.length === 0;
  if (state.modified) {
    return isValid ? 'modified valid' : 'modified invalid';
  }
  return isValid ? 'valid' : 'invalid';
}

export type EditFormFieldValidator = (value: string) => readonly string[];

export type EditFormModelValidationResults<TFieldName extends string> = Partial<
  Record<TFieldName, readonly string[]>
>;

export type UseEditFormOptions<TFieldName extends string> = {
  fieldValidators?: Partial<Record<TFieldName, EditFormFieldValidator>>;
  initialBaselineValues?: Partial<Record<TFieldName, string>>;
};

export type EditForm<TFieldName extends string> = {
  fieldCssClass: (field: TFieldName, additionalClassNames: string) => string;
  validationMessages: (field: TFieldName) => readonly string[];
  notifyFieldInput: (field: TFieldName, value: string) => void;
  notifyFieldBlur: (field: TFieldName) => void;
  validate: (validateModel: () => EditFormModelValidationResults<TFieldName>) => boolean;
  reset: (baselineValues?: Partial<Record<TFieldName, string>>) => void;
};

export function useEditForm<TFieldName extends string>(
  options?: UseEditFormOptions<TFieldName>,
): EditForm<TFieldName> {
  const fieldValidatorsRef = useRef(options?.fieldValidators);
  fieldValidatorsRef.current = options?.fieldValidators;

  const pendingValuesRef = useRef(new Map<TFieldName, string>());
  const committedValuesRef = useRef<Map<TFieldName, string> | null>(null);
  committedValuesRef.current ??= toBaselineMap(options?.initialBaselineValues);

  const [fieldStates, setFieldStates] = useState<Partial<Record<TFieldName, EditFormFieldState>>>(
    {},
  );

  const commitPendingValue = (field: TFieldName): string | null => {
    const committedValues = committedValuesRef.current!;
    const pending = pendingValuesRef.current.get(field);
    if (pending === undefined || pending === committedValues.get(field)) {
      return null;
    }
    committedValues.set(field, pending);
    return pending;
  };

  return {
    fieldCssClass: (field, additionalClassNames) => {
      const fieldClass = getFieldCssClass(fieldStates[field] ?? PRISTINE_FIELD_STATE);
      return additionalClassNames === '' ? fieldClass : `${additionalClassNames} ${fieldClass}`;
    },

    validationMessages: (field) => fieldStates[field]?.messages ?? [],

    notifyFieldInput: (field, value) => {
      pendingValuesRef.current.set(field, value);
    },

    notifyFieldBlur: (field) => {
      const committed = commitPendingValue(field);
      if (committed === null) {
        return;
      }
      const messages = fieldValidatorsRef.current?.[field]?.(committed) ?? [];
      setFieldStates((previous) => ({
        ...previous,
        [field]: { modified: true, messages },
      }));
    },

    validate: (validateModel) => {
      const committedNow = new Set<TFieldName>();
      for (const field of pendingValuesRef.current.keys()) {
        if (commitPendingValue(field) !== null) {
          committedNow.add(field);
        }
      }

      const results = validateModel();
      const isValid = Object.values<readonly string[] | undefined>(results).every(
        (messages) => messages === undefined || messages.length === 0,
      );

      setFieldStates((previous) => {
        const fields = new Set<TFieldName>([
          ...(Object.keys(previous) as TFieldName[]),
          ...(Object.keys(results) as TFieldName[]),
          ...committedNow,
        ]);
        const next: Partial<Record<TFieldName, EditFormFieldState>> = {};
        for (const field of fields) {
          next[field] = {
            modified: (previous[field]?.modified ?? false) || committedNow.has(field),
            messages: results[field] ?? [],
          };
        }
        return next;
      });

      return isValid;
    },

    reset: (baselineValues) => {
      pendingValuesRef.current.clear();
      committedValuesRef.current = toBaselineMap(baselineValues);
      setFieldStates({});
    },
  };
}
